/**
 * InvestmentsManager — multi-account tracker with expected returns.
 */
import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { NumberField, TextField, SelectField, Money, StatCell } from "./primitives";
import { useFinancialPlan } from "@/context/FinancialPlanContext";
import { Button } from "@/components/ui/button";

const ACCOUNT_TYPES = [
  ["brokerage", "Brokerage"], ["401k", "401(k)"], ["ira", "IRA"],
  ["tfsa", "TFSA (CA)"], ["rrsp", "RRSP (CA)"], ["fhsa", "FHSA (CA)"], ["resp", "RESP (CA)"],
  ["pension", "Pension"], ["crypto", "Crypto"], ["cash", "Cash / HYSA"], ["other", "Other"],
];

export default function InvestmentsManager() {
  const { plan, summary, patch } = useFinancialPlan();
  const investments = plan?.investments || [];
  const [busy, setBusy] = useState(false);

  const upsert = async (updated) => {
    setBusy(true);
    try { await patch({ investments: updated }); } finally { setBusy(false); }
  };
  const add = () => upsert([...investments, {
    id: `i_${Date.now()}`, name: "New account", account_type: "brokerage",
    current_balance: 0, monthly_contribution: 0, employer_match_pct: 0, expected_return_pct: 7,
    allocation: { stocks: 0, etfs: 0, mutual_funds: 0, bonds: 0, cash: 0, crypto: 0, real_estate: 0, alternatives: 0 },
  }]);
  const remove = (id) => upsert(investments.filter((i) => i.id !== id));
  const patchOne = (id, changes) => upsert(investments.map((i) => i.id === id ? { ...i, ...changes } : i));

  return (
    <div className="space-y-4" data-testid="investments-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <StatCell label="Total invested" value={<Money amount={summary?.total_investments} currency={plan?.currency} />} testId="inv-total" />
        <StatCell label="Monthly contributions" value={<Money amount={summary?.monthly_investing} currency={plan?.currency} />} testId="inv-monthly" />
        <StatCell label="Accounts" value={investments.length} sub={`${new Set(investments.map((i) => i.account_type)).size} types`} testId="inv-count" />
      </div>

      {investments.length === 0 && (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">
          Add your investment accounts — 401(k), IRA, brokerage, TFSA, RRSP, etc.
        </div>
      )}

      {investments.map((i) => (
        <div key={i.id} className="border border-border rounded-lg p-4 space-y-3" data-testid={`inv-row-${i.id}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextField label="Account name" value={i.name} onChange={(v) => patchOne(i.id, { name: v })} testId={`inv-name-${i.id}`} />
            <SelectField label="Type" value={i.account_type} onChange={(v) => patchOne(i.id, { account_type: v })} options={ACCOUNT_TYPES} testId={`inv-type-${i.id}`} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <NumberField label="Balance" value={i.current_balance} onChange={(v) => patchOne(i.id, { current_balance: v })} prefix={plan?.currency} testId={`inv-balance-${i.id}`} />
            <NumberField label="Monthly" value={i.monthly_contribution} onChange={(v) => patchOne(i.id, { monthly_contribution: v })} prefix={plan?.currency} testId={`inv-mc-${i.id}`} />
            <NumberField label="Employer match" value={i.employer_match_pct} onChange={(v) => patchOne(i.id, { employer_match_pct: v })} suffix="%" testId={`inv-match-${i.id}`} />
            <NumberField label="Expected return" value={i.expected_return_pct} onChange={(v) => patchOne(i.id, { expected_return_pct: v })} suffix="%" testId={`inv-return-${i.id}`} />
          </div>
          <div className="flex justify-end">
            <button onClick={() => remove(i.id)} className="text-xs text-destructive hover:underline flex items-center gap-1" data-testid={`inv-remove-${i.id}`}>
              <Trash2 size={12} /> Remove
            </button>
          </div>
        </div>
      ))}

      <Button size="sm" variant="outline" onClick={add} disabled={busy} data-testid="inv-add-btn">
        <Plus size={13} className="mr-1" /> Add investment account
      </Button>
    </div>
  );
}
